import { useCallback, useEffect, useRef, useState } from "react";

import { useComponentService } from "../../../../services/componentService";
import { Component, PendingComponentSubAssembly } from "../../../../types/component";
import { Airframe } from "../../../../types/airframe";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { useComponentMetadata } from "./useComponentMetadata";
import { filterOrphanedInstalledComponents } from "../../../../utils/componentWarehouse";
import { shouldShowComponentOwnershipFields } from "../../../../utils/ownershipVisibility";
import { buildComponentOwnershipPayload, mapComponentOwnershipFromApi } from "../../../../utils/componentOwnershipUtils";
import { buildLandingGearSerialFromSubAssemblies } from "../../../../utils/landingGearAttachUtils";
import { LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE } from "../../../../utils/componentFormValidation";

export type AttachmentListFilter = "attached" | "nonAttached";

// Map component type number/string to ComponentType
function mapComponentType(componentType: any): string {
  const typeMap: Record<number, string> = {
    0: "Engine",
    1: "APU",
    2: "LandingGear",
  };
  return typeMap[componentType] || componentType?.toString() || "Unknown";
}

// Helper function to lookup attachment info from airframes
function getAttachmentInfoFromAirframes(componentId: string, airframes: Airframe[]): { attachedToAirframe: string; attachmentLocation: string } {
  for (const airframe of airframes) {
    if (airframe.slots && Array.isArray(airframe.slots)) {
      for (const slot of airframe.slots) {
        if (slot.installedComponent && slot.installedComponent.componentId === componentId) {
          return {
            attachedToAirframe: airframe.tailNumber,
            attachmentLocation: slot.locationCode,
          };
        }
      }
    }
  }
  return { attachedToAirframe: "", attachmentLocation: "" };
}

export function useComponents(airframes: Airframe[] = []) {
  const [components, setComponents] = useState<Component[]>([]);
  const [attachmentListFilter, setAttachmentListFilter] =
    useState<AttachmentListFilter>("attached");
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; component: Component | null }>({ open: false, component: null });

  const airframesRef = useRef(airframes);
  useEffect(() => {
    airframesRef.current = airframes;
  }, [airframes]);

  const { showSuccess, showError } = useSnackbar();
  const {
    getComponents,
    getComponent,
    createComponent,
    updateComponent,
    deleteComponent,
    syncComponentSubAssemblies,
  } = useComponentService();  
  
  const { extractMetadata, buildDetailObjects: buildDetailObjectsFromHook } = useComponentMetadata();
  const load = useCallback(async () => {
    const frames = airframesRef.current;
    setLoading(true);
    try {
      const response = await getComponents({ attachment: attachmentListFilter });
      const data = Array.isArray(response) ? response : response?.data;
      
      if (Array.isArray(data)) {
        const nonDeletedComponents = data.filter((c: any) => !c.isDeleted);
        const parsedComponents = nonDeletedComponents.map((component: any) => {
          const attachedInfo = (!component.attachedToAirframe || component.attachedToAirframe === "") && frames.length > 0
            ? getAttachmentInfoFromAirframes(component.id, frames)
            : { attachedToAirframe: component.attachedToAirframe || "", attachmentLocation: component.attachmentLocation || "" };

          const ownership = mapComponentOwnershipFromApi(component);

          return {
            ...component,
            dateManufactured: component?.dateOfManufacture,
            type: mapComponentType(component?.componentType),
            ownershipStatus:
              ownership.ownershipStatus === "" ? null : ownership.ownershipStatus,
            ownershipStatusLabel: ownership.ownershipStatusLabel,
            operatorId: ownership.operatorId,
            operatorName: ownership.operatorName,
            owner: ownership.owner,
            customMetadata: extractMetadata(component),
            attachedToAirframe: attachedInfo.attachedToAirframe,
            attachmentLocation: attachedInfo.attachmentLocation,
          };
        });

        setComponents(filterOrphanedInstalledComponents(parsedComponents, frames));
      } else {
        showError("Invalid response: expected an array of assemblies");
      }
    } catch (error) {
      showError("Failed to load assemblies");
    } finally {
      setLoading(false);
    }
  }, [getComponents, showError, extractMetadata, attachmentListFilter]);

const save = useCallback(
  async (
    data: Partial<Component> & {
      hasSubAssembly?: boolean;
      pendingSubAssemblies?: PendingComponentSubAssembly[];
    },
    id?: string
  ): Promise<string | undefined> => {
    try {
      let dateToSend = data.dateManufactured;
      if (dateToSend && typeof dateToSend === "string" && !dateToSend.includes("T")) {
        dateToSend = new Date(dateToSend).toISOString().split("T")[0];
      }

      const customMetadata: Record<string, unknown> = {
        ...(data.customMetadata ?? {}),
        ...(data.type === "LandingGear"
          ? { hasSubAssembly: Boolean(data.hasSubAssembly) }
          : {}),
      };

      const { engineDetails, apuDetails, landingGearDetails } = buildDetailObjectsFromHook(
        data.type ?? "",
        customMetadata
      );

      const ownershipPayload = () => {
        if (
          !shouldShowComponentOwnershipFields(
            data.attachedToAirframe,
            data.attachmentLocation
          )
        ) {
          return {};
        }
        return buildComponentOwnershipPayload(data);
      };

      const isLandingGear = data.type === "LandingGear";
      const pendingSubAssemblies = data.pendingSubAssemblies ?? [];
      const airframeFamily = String(customMetadata.lgAirframeFamily ?? "").trim();

      if (isLandingGear && data.hasSubAssembly) {
        if (pendingSubAssemblies.length === 0) {
          showError(LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE);
          return undefined;
        }

        if (!airframeFamily) {
          showError("Airframe type is required when landing gear has sub-assemblies");
          return undefined;
        }

        data = {
          ...data,
          serialNumber: buildLandingGearSerialFromSubAssemblies(pendingSubAssemblies),
        };
      }

      const subAssemblyIds =
        isLandingGear && data.hasSubAssembly
          ? pendingSubAssemblies.map((item) => item.id)
          : [];

      let componentId = id;

      if (id) {
        const updateRequest = {
          componentType: data.type ?? "",
          manufacturer: data.manufacturer ?? "",
          model: data.model ?? "",
          serialNumber: data.serialNumber ?? "",
          ...(dateToSend ? { DateManufactured: dateToSend } : {}),
          attachedToAirframe: data.attachedToAirframe,
          attachmentLocation: data.attachmentLocation,
          customMetadata,
          engine: engineDetails,
          apu: apuDetails,
          landingGear: landingGearDetails,
          ...ownershipPayload(),
        };

        const updateResult = await updateComponent(id, updateRequest);
        if (!updateResult) {
          throw new Error("Update failed");
        }
      } else {
        if (!data.serialNumber || !data.model || !data.manufacturer) {
          showError("Serial Number, Model, and Manufacturer are required");
          return undefined;
        }

        const createRequest = {
          componentType: data.type ?? "",
          serialNumber: data.serialNumber,
          model: data.model,
          manufacturer: data.manufacturer,
          ...(dateToSend ? { dateManufactured: dateToSend } : {}),
          attachedToAirframe: data.attachedToAirframe,
          attachmentLocation: data.attachmentLocation,
          customMetadata,
          engineDetails,
          apuDetails,
          landingGearDetails,
          isDeleted: false,
          createdDate: new Date().toISOString(),
          ...ownershipPayload(),
        };

        const createResult = await createComponent(createRequest);
        if (!createResult) {
          throw new Error("Create failed");
        }
        
        const createdId =
          (createResult as any)?.data?.id ??
          (createResult as any)?.id ??
          null;
        if (!createdId) {
          throw new Error("Create failed");
        }
        componentId = createdId;
      }

      if (isLandingGear && componentId) {
        await syncComponentSubAssemblies(componentId, subAssemblyIds);
      }

      showSuccess(`Assembly ${id ? "updated" : "created"} successfully`);
      await load();
      return componentId;
    } catch (err) {
      const fallback = `Failed to ${id ? "update" : "create"} assembly`;
      const raw =
        err instanceof Error && err.message.trim().length > 0
          ? err.message.trim()
          : "";
      const message =
        raw.replace(/\s*CorrelationId=[^\s]+$/i, "").trim() || fallback;
      showError(message);
      return undefined;
    }
  },
  [
    createComponent,
    updateComponent,
    syncComponentSubAssemblies,
    showSuccess,
    showError,
    load,
    buildDetailObjectsFromHook,
  ]
);

  const remove = useCallback(
    async (component: Component) => {
      if (!component.id) return;
      setConfirmDelete({ open: true, component });
    },
    []
  );

  const handleConfirmDelete = useCallback(
    async () => {
      const component = confirmDelete.component;
      if (!component || !component.id) return;

      setIsDeleting(true);
      try {
        const deleteResult = await deleteComponent(component.id);
        if (deleteResult) {
          showSuccess("Assembly deleted successfully");
          setConfirmDelete({ open: false, component: null });
          await load();
        } else {
          showError("Failed to delete assembly");
          setConfirmDelete({ open: false, component: null });
        }
      } catch (err) {
        showError("Failed to delete assembly");
        setConfirmDelete({ open: false, component: null });
      } finally {
        setIsDeleting(false);
      }
    },
    [confirmDelete.component, deleteComponent, showSuccess, showError, load]
  );

  const fetchById = useCallback(
    async (id: string): Promise<Component | null> => {
      try {
        const response = await getComponent(id);
        let component: any;
        if (Array.isArray(response)) {
          component = response[0];
        } else if (response && typeof response === "object" && "data" in response && !("id" in response)) {
          component = Array.isArray(response.data) ? response.data[0] : response.data;
        } else {
          component = response;
        }
        if (component && typeof component === "object") {
          const componentId = component.id ?? component.Id;
          if (!componentId) return null;

          // Try to get attachment info from backend first
          let attachedToAirframe = component.attachedToAirframe || "";
          let attachmentLocation = component.attachmentLocation || "";
          
          // If not provided by backend, look up from airframes slots
          if ((!attachedToAirframe || attachedToAirframe === "") && airframes.length > 0) {
            const info = getAttachmentInfoFromAirframes(String(componentId), airframes);
            attachedToAirframe = info.attachedToAirframe;
            attachmentLocation = info.attachmentLocation;
          }

          const ownership = mapComponentOwnershipFromApi(component);
          
          const parsed: Component = {
            ...component,
            id: String(componentId),
            dateManufactured: component.dateOfManufacture ?? component.dateManufactured,
            type: mapComponentType(component.componentType ?? component.ComponentType),
            ownershipStatus:
              ownership.ownershipStatus === "" ? null : ownership.ownershipStatus,
            ownershipStatusLabel: ownership.ownershipStatusLabel,
            operatorId: ownership.operatorId,
            operatorName: ownership.operatorName,
            owner: ownership.owner,
            customMetadata: extractMetadata(component),
            attachedToAirframe,
            attachmentLocation,
          } as Component;
          return parsed;
        }
        return null;
      } catch (error) {
        showError("Failed to fetch assembly details");
        return null;
      }
    },
    [getComponent, showError, airframes, extractMetadata]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (airframes.length === 0) return;

    setComponents((prevComponents) => {
      const refreshed = prevComponents.map((component) => {
        if (!component || component.attachedToAirframe) return component;

        const info = getAttachmentInfoFromAirframes(component.id, airframes);
        if (!info.attachedToAirframe && !info.attachmentLocation) {
          return component;
        }

        return {
          ...component,
          attachedToAirframe: info.attachedToAirframe,
          attachmentLocation: info.attachmentLocation,
        };
      });

      return filterOrphanedInstalledComponents(refreshed, airframes);
    });
  }, [airframes]);

  // Calculate free components (not deleted and not attached to any airframe)
  const freeComponents = components.filter(
    (c) => c && c.isDeleted === false && (!c.attachedToAirframe || c.attachedToAirframe === "")
  );

  return { 
    components, 
    loading, 
    save, 
    remove, 
    fetchById,
    freeComponentsCount: freeComponents.length,
    freeComponents,
    confirmDelete,
    isDeleting,
    setConfirmDelete,
    handleConfirmDelete,
    attachmentListFilter,
    setAttachmentListFilter,
  };
}
